import axios, { AxiosError } from 'axios';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import { getChatModelsConfigSync } from '../../hooks/useChatModels';
import { CHAT_MODELS_CONFIG } from '../../config/models';
import { getAuthHeaders, clearAuthData } from '../../utils/authHeaders';

// Базовая конфигурация API
// Пустая строка = использовать текущий домен (работает и на ngrok и на production)
export const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Retry configuration
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 500, // ms
  maxDelay: 5000, // ms
  retryOn: [502, 503, 504], // Bad Gateway, Service Unavailable, Gateway Timeout
  // Note: 429 (Too Many Requests) is NOT retried - retrying rate limits just makes things worse
};

/**
 * Sleep function
 */
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Calculate retry delay with exponential backoff
 */
function getRetryDelay(attempt: number): number {
  const exponentialDelay = RETRY_CONFIG.baseDelay * Math.pow(2, attempt);
  const jitter = Math.random() * 200;
  return Math.min(exponentialDelay + jitter, RETRY_CONFIG.maxDelay);
}

/**
 * Базовый API клиент с общей конфигурацией axios
 */
export class BaseApiClient {
  protected client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 1200000, // 20 минут для длинных запросов (video2video/veo/sora могут занять до 10-12 минут)
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Настройка interceptors для авторизации и обработки ошибок
   */
  private setupInterceptors() {
    // Interceptor для добавления Telegram initData в заголовки
    // Поддерживает как Telegram WebApp, так и Login Widget (данные из localStorage)
    this.client.interceptors.request.use((config) => {
      const authHeaders = getAuthHeaders();
      
      // Добавляем заголовки авторизации и платформы
      if (authHeaders['X-Telegram-Init-Data']) {
        config.headers['X-Telegram-Init-Data'] = authHeaders['X-Telegram-Init-Data'];
      }
      if (authHeaders['X-Session-ID']) {
        config.headers['X-Session-ID'] = authHeaders['X-Session-ID'];
        // Дублируем session_id в Authorization Bearer для надёжности
        config.headers['Authorization'] = `Bearer ${authHeaders['X-Session-ID']}`;
      }
      if (authHeaders['X-Platform-Type']) {
        config.headers['X-Platform-Type'] = authHeaders['X-Platform-Type'];
      }
      
      // Для FormData удаляем Content-Type, чтобы axios установил правильный с boundary
      if (config.data instanceof FormData) {
        delete config.headers['Content-Type'];
      }
      
      return config;
    });

    // Interceptor для обработки ошибок с retry logic
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const config = error.config as AxiosRequestConfig & { _retryCount?: number };
        
        // Initialize retry count
        if (config._retryCount === undefined) {
          config._retryCount = 0;
        }

        // Check if we should retry
        const status = error.response?.status;
        const shouldRetry = status && RETRY_CONFIG.retryOn.includes(status) && config._retryCount < RETRY_CONFIG.maxRetries;

        if (shouldRetry) {
          config._retryCount++;
          const delay = getRetryDelay(config._retryCount - 1);
          await sleep(delay);
          return this.client.request(config);
        }

        // Обработка 401 ошибки - данные авторизации устарели или невалидны
        if (error.response?.status === 401) {
          // Проверяем, запущено ли приложение внутри Telegram Mini App
          const webAppData = (window as any).Telegram?.WebApp?.initData;
          const isInMiniApp = typeof webAppData === 'string' && webAppData.length > 0;

          if (isInMiniApp) {
            // В Mini App НЕ редиректим на login - OAuth widget не работает в iframe
            console.error('❌ Auth error in Mini App (401)');
            const webApp = (window as any).Telegram?.WebApp;
            if (webApp?.showPopup) {
              webApp.showPopup({
                title: 'Ошибка авторизации',
                message: 'Пожалуйста, закройте и откройте приложение заново.',
                buttons: [{ type: 'close' }]
              });
            }
          } else {
            // НЕ редиректим на публичных страницах (лендинг, цены, логин)
            const publicPages = ['/', '/login', '/pricing', '/public-pricing', '/access-denied'];
            const isPublicPage = publicPages.includes(window.location.pathname) ||
                                 window.location.pathname.startsWith('/login') ||
                                 window.location.pathname.startsWith('/access-denied');

            if (!isPublicPage) {
              // Очищаем данные и редиректим ТОЛЬКО на защищённых страницах
              // КРИТИЧНО: clearAuthData() НЕ вызывается на публичных страницах,
              // чтобы не затереть свежий session_id при транзитном 401 во время инициализации
              clearAuthData();
              localStorage.removeItem('user-storage');
              window.location.href = '/login';
            }
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  /**
   * Конвертация File в base64 строку
   */
  protected fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Проверка поддержки vision моделью
   */
  protected checkModelSupportsVision(provider: string, model: string): boolean {
    const config = getChatModelsConfigSync();
    const providerModels = config[provider] || CHAT_MODELS_CONFIG[provider];
    if (!providerModels) return false;
    
    const modelConfig = providerModels.find((m: any) => m.value === model);
    return modelConfig?.supportsVision || false;
  }
}

